import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  Copy,
  Loader2,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { request } from "../lib/api";
import { getAdminRole } from "../lib/auth";
import { useAttribution } from "../lib/useAttribution";
import { useAudienceChecks } from "../lib/useAudienceChecks";
import { audienceNote, useAudienceRules } from "../lib/useAudienceRules";
import type {
  AudienceRule,
  ContentItem,
  CreatedResponse,
  Notice,
  PDDayDetail as PDDayDetailType,
  PDDayModule,
} from "../types";
import { CONTENT_TYPE_LABELS } from "../types";
import type { Attribution } from "../types";
import { ContentEditor } from "../components/ContentEditor";
import { AttributionCell } from "../components/Attribution";
import { AudienceModal } from "../components/AudienceModal";
import { DragList } from "../components/DragList";
import { ContentTranslationEditor } from "./ContentTranslation";
import {
  Badge,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  InlineNotice,
  LoadingState,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../components/ui";

// What the right-hand panel shows. The whole PD day is built on this one
// page: its modules on the left, each with its content, and whichever
// thing is being edited on the right.
type Pane =
  | { kind: "day" }
  | { kind: "newModule" }
  | { kind: "module"; moduleID: string }
  | { kind: "item"; moduleID: string; itemID: string | null }
  | { kind: "translate"; moduleID: string; itemID: string };

interface ItemsResponse {
  items: ContentItem[];
}

export function PDDayDetail() {
  const isSuperAdmin = getAdminRole() === "super_admin";
  const { id } = useParams<{ id: string }>();
  const [day, setDay] = useState<PDDayDetailType | null>(null);
  const [error, setError] = useState("");
  const [items, setItems] = useState<Record<string, ContentItem[]>>({});
  const [pane, setPane] = useState<Pane>({ kind: "day" });
  const [message, setMessage] = useState<Notice | null>(null);
  const [reordering, setReordering] = useState(false);
  const [confirmModule, setConfirmModule] = useState<PDDayModule | null>(null);
  const [confirmItem, setConfirmItem] = useState<ContentItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    request<PDDayDetailType>(`/api/admin/pd-days/${id}`)
      .then((data) => {
        setDay(data);
        setError("");
      })
      .catch((cause) => setError(errorMessage(cause)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const loadItems = useCallback((moduleID: string) => {
    request<ItemsResponse>(`/api/admin/content-items?module_id=${moduleID}`)
      .then((data) =>
        setItems((current) => ({
          ...current,
          [moduleID]: data.items.sort((a, b) => a.position - b.position),
        }))
      )
      .catch(() => setItems((current) => ({ ...current, [moduleID]: [] })));
  }, []);
  const moduleKey = day?.modules.map((module) => module.id).join(",") ?? "";
  const audienceChecks = useAudienceChecks(day?.modules.map((module) => module.id) ?? []);
  const audienceRules = useAudienceRules(day?.modules.map((module) => module.id) ?? []);
  const [audienceFor, setAudienceFor] = useState<{
    type: "module" | "content_item";
    id: string;
    title: string;
  } | null>(null);
  const dayAttribution = useAttribution("pd-days", day ? [day.id] : []);
  const moduleAttribution = useAttribution(
    "modules",
    day?.modules.map((module) => module.id) ?? []
  );
  const itemAttribution = useAttribution(
    "content-items",
    Object.values(items).flatMap((list) => list.map((item) => item.id))
  );
  useEffect(() => {
    moduleKey.split(",").filter(Boolean).forEach(loadItems);
  }, [moduleKey, loadItems]);

  // After an item changes: its module's list, and the PD day's counts.
  function refreshModule(moduleID: string) {
    loadItems(moduleID);
    load();
  }

  async function moveModule(index: number, offset: number) {
    if (!day || !id) return;
    const ids = day.modules.map((module) => module.id);
    const target = index + offset;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setReordering(true);
    setMessage(null);
    try {
      await request(`/api/admin/pd-days/${id}/modules/reorder`, {
        method: "POST",
        body: JSON.stringify({ module_ids: ids }),
      });
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setReordering(false);
    }
  }

  async function reorderItems(moduleID: string, next: ContentItem[]) {
    setItems((current) => ({ ...current, [moduleID]: next }));
    try {
      await request("/api/admin/content-items/reorder", {
        method: "POST",
        body: JSON.stringify({ module_id: moduleID, item_ids: next.map((item) => item.id) }),
      });
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
    loadItems(moduleID);
  }

  async function duplicateItem(moduleID: string, itemID: string) {
    try {
      await request(`/api/admin/content-items/${itemID}/duplicate`, { method: "POST" });
      setMessage({ tone: "success", text: "Item duplicated." });
      refreshModule(moduleID);
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  async function moveItem(fromModuleID: string, itemID: string, toModuleID: string) {
    setMessage(null);
    try {
      await request(`/api/admin/content-items/${itemID}/move`, {
        method: "POST",
        body: JSON.stringify({ module_id: toModuleID }),
      });
      setMessage({ tone: "success", text: "Item moved." });
      if ((pane.kind === "item" || pane.kind === "translate") && pane.itemID === itemID) {
        setPane({ kind: "day" });
      }
      loadItems(fromModuleID);
      refreshModule(toModuleID);
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  async function deleteModule(module: PDDayModule) {
    setDeleting(true);
    setMessage(null);
    try {
      await request(`/api/admin/modules/${module.id}/permanent`, { method: "DELETE" });
      setMessage({ tone: "success", text: `"${module.title}" deleted.` });
      if (pane.kind !== "day" && pane.kind !== "newModule" && pane.moduleID === module.id) {
        setPane({ kind: "day" });
      }
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setDeleting(false);
      setConfirmModule(null);
    }
  }

  async function deleteItem(item: ContentItem) {
    setDeleting(true);
    setMessage(null);
    const moduleID = Object.keys(items).find((key) =>
      items[key].some((entry) => entry.id === item.id)
    );
    try {
      await request(`/api/admin/content-items/${item.id}`, { method: "DELETE" });
      setMessage({ tone: "success", text: "Item deleted." });
      if ((pane.kind === "item" || pane.kind === "translate") && pane.itemID === item.id) {
        setPane({ kind: "day" });
      }
      if (moduleID) refreshModule(moduleID);
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setDeleting(false);
      setConfirmItem(null);
    }
  }

  if (error) return <ErrorState message={error} />;
  if (!day) return <LoadingState message="Loading PD day…" />;

  const findItem = (itemID: string) =>
    Object.values(items)
      .flat()
      .find((item) => item.id === itemID);

  return (
    <div className="grid gap-6 xl:grid-cols-[440px_minmax(0,1fr)]">
      <div className="flex flex-wrap items-center justify-between gap-3 xl:col-span-2">
        <Link
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-content-secondary hover:text-interactive"
          to="/pd-days"
        >
          <ChevronLeft size={16} />
          PD days
        </Link>
        <div className="flex flex-wrap items-center gap-3 text-sm text-content-secondary">
          <Badge tone={day.status === "published" ? "success" : "neutral"}>{day.status}</Badge>
          <span>{day.cohort}</span>
          <span>
            Scheduled {day.session_count ? `${day.session_count}×` : "not yet"}
            {isSuperAdmin && (
              <Link className="ml-2 font-semibold text-interactive hover:underline" to="/schedule">
                Schedule
              </Link>
            )}
          </span>
        </div>
      </div>

      <div className="min-w-0">
        <Card
          title={day.title}
          text={`${day.modules.length} ${day.modules.length === 1 ? "module" : "modules"}. Build each module's content here; teachers go through them in order.`}
        >
          <div className="mb-4">
            <AttributionCell info={dayAttribution[day.id]} />
          </div>
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              className={`${secondaryButtonClass} inline-flex items-center gap-1.5`}
              onClick={() => setPane({ kind: "day" })}
            >
              <Settings2 size={15} />
              PD day settings
            </button>
          </div>
          {message && (
            <div className="mb-4 [&>p]:mt-0">
              <InlineNotice tone={message.tone} text={message.text} />
            </div>
          )}
          {!day.modules.length && (
            <EmptyState title="No modules yet" message="Add the first module, then its content." />
          )}
          <div className="grid gap-4">
            {day.modules.map((module, index) => (
              <section
                key={module.id}
                className={`min-w-0 rounded-lg border p-3 ${pane.kind !== "day" && pane.kind !== "newModule" && pane.moduleID === module.id ? "border-interactive" : "border-border"}`}
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
                      Module {index + 1}
                    </span>
                    <button
                      type="button"
                      className="block max-w-full truncate text-start font-semibold text-content-primary hover:text-interactive"
                      onClick={() => setPane({ kind: "module", moduleID: module.id })}
                    >
                      {module.title}
                    </button>
                    <Badge tone={module.status === "published" ? "success" : "neutral"}>
                      {module.status}
                    </Badge>
                    {audienceNote(audienceRules[module.id]) && (
                      <p className="mt-1 text-xs font-medium text-interactive">
                        {audienceNote(audienceRules[module.id])}
                      </p>
                    )}
                    <div className="mt-1.5">
                      <AttributionCell info={moduleAttribution[module.id]} />
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="text-content-secondary hover:text-interactive disabled:opacity-30"
                      onClick={() => void moveModule(index, -1)}
                      disabled={reordering || index === 0}
                      aria-label={`Move ${module.title} up`}
                    >
                      <ArrowUp size={15} />
                    </button>
                    <button
                      type="button"
                      className="text-content-secondary hover:text-interactive disabled:opacity-30"
                      onClick={() => void moveModule(index, 1)}
                      disabled={reordering || index === day.modules.length - 1}
                      aria-label={`Move ${module.title} down`}
                    >
                      <ArrowDown size={15} />
                    </button>
                    <button
                      type="button"
                      className="text-xs font-semibold text-interactive hover:underline"
                      onClick={() =>
                        setAudienceFor({ type: "module", id: module.id, title: module.title })
                      }
                      aria-label={`Who sees ${module.title}`}
                    >
                      Audience
                    </button>
                    <button
                      type="button"
                      className="text-content-secondary hover:text-interactive"
                      onClick={() => setPane({ kind: "module", moduleID: module.id })}
                      aria-label={`Edit ${module.title}`}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      className="text-content-secondary hover:text-danger"
                      onClick={() => setConfirmModule(module)}
                      aria-label={`Delete ${module.title}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                {audienceChecks[module.id]?.at_risk && (
                  <p
                    role="alert"
                    className="mb-3 rounded-md bg-warning-bg px-3 py-2 text-xs leading-5 text-warning-text"
                  >
                    {audienceChecks[module.id].teachers_without_group} of{" "}
                    {audienceChecks[module.id].teachers} teachers are in no study group. They see
                    the items with no audience rule, but none of the items shown to a group:{" "}
                    {audienceChecks[module.id].group_targeted_items
                      .map((item) => item.title)
                      .join(", ") || "the whole module"}
                    . Put them in a group before this goes out.
                  </p>
                )}
                <ModuleItems
                  items={items[module.id]}
                  attribution={itemAttribution}
                  audience={audienceRules}
                  onAudience={(item) =>
                    setAudienceFor({ type: "content_item", id: item.id, title: item.title })
                  }
                  selectedID={
                    (pane.kind === "item" || pane.kind === "translate") &&
                    pane.moduleID === module.id
                      ? pane.itemID
                      : null
                  }
                  onReorder={(next) => void reorderItems(module.id, next)}
                  onAdd={() => setPane({ kind: "item", moduleID: module.id, itemID: null })}
                  onEdit={(itemID) => setPane({ kind: "item", moduleID: module.id, itemID })}
                  onTranslate={(itemID) =>
                    setPane({ kind: "translate", moduleID: module.id, itemID })
                  }
                  onDuplicate={(itemID) => void duplicateItem(module.id, itemID)}
                  onDelete={(item) => setConfirmItem(item)}
                  moveTargets={day.modules
                    .map((target, targetIndex) => ({
                      id: target.id,
                      label: `Module ${targetIndex + 1}: ${target.title}`,
                    }))
                    .filter((target) => target.id !== module.id)}
                  onMove={(itemID, toModuleID) => void moveItem(module.id, itemID, toModuleID)}
                />
              </section>
            ))}
          </div>
          <button
            type="button"
            className={`${primaryButtonClass} mt-4 inline-flex w-full items-center justify-center gap-1.5`}
            onClick={() => setPane({ kind: "newModule" })}
          >
            <Plus size={16} />
            Add module
          </button>
        </Card>
      </div>

      <div className="xl:sticky xl:top-6 xl:self-start">
        {pane.kind === "day" ? (
          <DaySettings day={day} onSaved={load} />
        ) : pane.kind === "newModule" ? (
          <NewModule
            pdDayID={day.id}
            onCreated={(moduleID) => {
              load();
              // Straight on to the new module's first piece of content.
              setPane({ kind: "item", moduleID, itemID: null });
            }}
            onCancel={() => setPane({ kind: "day" })}
          />
        ) : pane.kind === "module" ? (
          <ModuleSettings
            key={pane.moduleID}
            moduleID={pane.moduleID}
            onSaved={() => {
              setMessage({ tone: "success", text: "Module updated." });
              load();
            }}
            onCancel={() => setPane({ kind: "day" })}
          />
        ) : pane.kind === "translate" ? (
          <ContentTranslationEditor
            key={pane.itemID}
            id={pane.itemID}
            onClose={() => setPane({ kind: "day" })}
          />
        ) : (
          <ContentEditor
            key={`${pane.moduleID}:${pane.itemID ?? "new"}`}
            moduleID={pane.moduleID}
            itemID={pane.itemID}
            onSaved={() => {
              setMessage({ tone: "success", text: "Content saved." });
              refreshModule(pane.moduleID);
              setPane({ kind: "day" });
            }}
            onCancel={() => setPane({ kind: "day" })}
            onRequestDelete={(itemID) => {
              const item = findItem(itemID);
              if (item) setConfirmItem(item);
            }}
          />
        )}
      </div>

      <ConfirmModal
        open={!!confirmModule}
        title="Delete module?"
        message={`"${confirmModule?.title}" and all its content items will be permanently deleted, along with teacher progress and responses for them. This cannot be undone.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={() => confirmModule && deleteModule(confirmModule)}
        onCancel={() => setConfirmModule(null)}
      />
      <ConfirmModal
        open={!!confirmItem}
        title="Delete content item?"
        message={`"${confirmItem?.title}" and its content will be permanently deleted.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={() => confirmItem && deleteItem(confirmItem)}
        onCancel={() => setConfirmItem(null)}
      />
      {audienceFor && (
        <AudienceModal
          resourceType={audienceFor.type}
          resourceID={audienceFor.id}
          title={audienceFor.title}
          onClose={() => setAudienceFor(null)}
        />
      )}
    </div>
  );
}

// One module's content, in teacher order: drag to reorder, click to edit.
function ModuleItems({
  items,
  attribution,
  audience,
  onAudience,
  selectedID,
  onReorder,
  onAdd,
  onEdit,
  onTranslate,
  onDuplicate,
  onDelete,
  moveTargets,
  onMove,
}: {
  moveTargets: { id: string; label: string }[];
  onMove: (itemID: string, toModuleID: string) => void;
  items: ContentItem[] | undefined;
  attribution: Record<string, Attribution>;
  audience: Record<string, AudienceRule[]>;
  onAudience: (item: ContentItem) => void;
  selectedID: string | null;
  onReorder: (next: ContentItem[]) => void;
  onAdd: () => void;
  onEdit: (itemID: string) => void;
  onTranslate: (itemID: string) => void;
  onDuplicate: (itemID: string) => void;
  onDelete: (item: ContentItem) => void;
}) {
  if (!items) return <p className="text-sm text-content-secondary">Loading content…</p>;
  if (!items.length) {
    return (
      <button
        type="button"
        className={`${secondaryButtonClass} inline-flex w-full items-center justify-center gap-1.5`}
        onClick={onAdd}
      >
        <Plus size={15} />
        Add content
      </button>
    );
  }
  return (
    <DragList
      items={items}
      onChange={onReorder}
      onRemove={(index) => onDelete(items[index])}
      addLabel="Add content"
      onAdd={onAdd}
      renderItem={(item) => (
        <div className="flex min-w-0 flex-col gap-2">
          <div className="min-w-0">
            <button
              type="button"
              className={`block max-w-full truncate text-start text-sm font-semibold hover:text-interactive ${item.id === selectedID ? "text-interactive" : "text-content-primary"}`}
              onClick={() => onEdit(item.id)}
            >
              {item.title}
            </button>
            <p className="truncate text-xs text-content-secondary">
              {CONTENT_TYPE_LABELS[item.type]} · {item.status}
              {item.required ? " · required" : ""}
            </p>
            {audienceNote(audience[item.id]) && (
              <p className="mt-0.5 text-xs font-medium text-interactive">
                {audienceNote(audience[item.id])}
              </p>
            )}
            <div className="mt-1">
              <AttributionCell info={attribution[item.id]} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {item.type === "quiz" && (
              <Link
                className="text-xs font-semibold text-interactive hover:underline"
                to={`/content/${item.id}/questions`}
              >
                Questions
              </Link>
            )}
            {item.type === "form" && (
              <Link
                className="text-xs font-semibold text-interactive hover:underline"
                to={`/content/${item.id}/fields`}
              >
                Fields
              </Link>
            )}
            <button
              type="button"
              className="text-xs font-semibold text-interactive hover:underline"
              onClick={() => onAudience(item)}
              aria-label={`Who sees ${item.title}`}
            >
              Audience
            </button>
            <button
              type="button"
              className="text-xs font-semibold text-interactive hover:underline"
              onClick={() => onTranslate(item.id)}
            >
              اردو
            </button>
            <button
              type="button"
              className="text-content-secondary hover:text-interactive"
              onClick={() => onDuplicate(item.id)}
              aria-label="Duplicate"
            >
              <Copy size={15} />
            </button>
            {moveTargets.length > 0 && (
              <select
                className="ms-auto w-28 rounded border border-border bg-surface px-1 py-0.5 text-xs text-content-secondary"
                value=""
                aria-label={`Move ${item.title} to another module`}
                onChange={(event) => event.target.value && onMove(item.id, event.target.value)}
              >
                <option value="">Move to…</option>
                {moveTargets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
    />
  );
}

function DaySettings({ day, onSaved }: { day: PDDayDetailType; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Notice | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const {
      title_ur: titleUr,
      description_ur: descriptionUr,
      ...data
    } = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await request(`/api/admin/pd-days/${day.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...data,
          sequence: Number(data.sequence),
          translations: { ur: { title: titleUr, description: descriptionUr } },
        }),
      });
      setMessage({ tone: "success", text: "PD day updated." });
      onSaved();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="PD day settings" text="Pick a module or a content item on the left to edit it.">
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit} key={day.id}>
        <label className={`${labelClass} sm:col-span-2`}>
          Title
          <input className={inputClass} name="title" defaultValue={day.title} required />
        </label>
        <label className={`${labelClass} sm:col-span-2`}>
          Description
          <input className={inputClass} name="description" defaultValue={day.description ?? ""} />
        </label>
        <label className={labelClass}>
          Title (Urdu)
          <input
            className={inputClass}
            name="title_ur"
            dir="rtl"
            defaultValue={day.translations?.ur?.title ?? ""}
          />
        </label>
        <label className={labelClass}>
          Description (Urdu)
          <input
            className={inputClass}
            name="description_ur"
            dir="rtl"
            defaultValue={day.translations?.ur?.description ?? ""}
          />
        </label>
        <label className={labelClass}>
          Order in cohort
          <input
            className={inputClass}
            name="sequence"
            type="number"
            min={1}
            defaultValue={day.sequence}
            required
          />
        </label>
        <label className={labelClass}>
          Status
          <select className={inputClass} name="status" defaultValue={day.status}>
            <option>draft</option>
            <option>published</option>
            <option>archived</option>
          </select>
        </label>
        <button className={`${primaryButtonClass} sm:col-span-2`} disabled={busy}>
          {busy && <Loader2 size={16} className="animate-spin" />}Save PD day
        </button>
      </form>
      {message && <InlineNotice tone={message.tone} text={message.text} />}
      {day.status === "published" && !day.session_count && (
        <p className="mt-4 text-xs text-content-secondary">
          Teachers see this PD day only once it is scheduled at a centre.
        </p>
      )}
    </Card>
  );
}

function NewModule({
  pdDayID,
  onCreated,
  onCancel,
}: {
  pdDayID: string;
  onCreated: (moduleID: string) => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Notice | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      const result = await request<CreatedResponse>("/api/admin/modules", {
        method: "POST",
        body: JSON.stringify({
          pd_day_id: pdDayID,
          title: data.title,
          description: data.description,
          status: data.status,
          estimated_minutes: data.estimated_minutes ? Number(data.estimated_minutes) : null,
          translations: { ur: { title: data.title_ur, description: "" } },
        }),
      });
      onCreated(result.id);
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
      setBusy(false);
    }
  }
  return (
    <Card title="Add module" text="Added after the last module; its content comes next.">
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
        <div className="sm:col-span-2">
          <Field name="title" />
        </div>
        <div className="sm:col-span-2">
          <Field name="description" required={false} />
        </div>
        <label className={`${labelClass} sm:col-span-2`}>
          Title (Urdu, optional)
          <input className={inputClass} name="title_ur" dir="rtl" />
        </label>
        <label className={labelClass}>
          Status
          <select className={inputClass} name="status">
            <option>draft</option>
            <option>published</option>
          </select>
        </label>
        <Field name="estimated_minutes" type="number" required={false} />
        <div className="flex items-center gap-3 sm:col-span-2">
          <button className={primaryButtonClass} disabled={busy}>
            {busy && <Loader2 size={16} className="animate-spin" />}Add module and its content
          </button>
          <button type="button" className={secondaryButtonClass} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
      {message && <InlineNotice tone={message.tone} text={message.text} />}
    </Card>
  );
}

interface ModuleRecord {
  id: string;
  title: string;
  description?: string | null;
  sequence: number;
  status: string;
  locale: string;
  cover_image_url?: string | null;
  estimated_minutes?: number | null;
  translations?: Record<string, { title?: string; description?: string } | undefined>;
}

// A module's own details. Loaded in full first: the update replaces every
// field, so fields not shown here (cover image, locale) are sent back as is.
function ModuleSettings({
  moduleID,
  onSaved,
  onCancel,
}: {
  moduleID: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [module, setModule] = useState<ModuleRecord | null>(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Notice | null>(null);
  useEffect(() => {
    request<ModuleRecord>(`/api/admin/modules/${moduleID}`)
      .then(setModule)
      .catch((cause) => setLoadError(errorMessage(cause)));
  }, [moduleID]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!module) return;
    setBusy(true);
    setMessage(null);
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await request(`/api/admin/modules/${moduleID}`, {
        method: "PUT",
        body: JSON.stringify({
          title: data.title,
          description: data.description,
          status: data.status,
          sequence: module.sequence,
          locale: module.locale,
          cover_image_url: module.cover_image_url || null,
          estimated_minutes: data.estimated_minutes ? Number(data.estimated_minutes) : null,
          translations: { ur: { title: data.title_ur, description: data.description_ur } },
        }),
      });
      setMessage({ tone: "success", text: "Module updated." });
      onSaved();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <ErrorState message={loadError} />;
  if (!module) return <LoadingState message="Loading module…" />;
  return (
    <Card title="Module settings" text={module.title}>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
        <label className={`${labelClass} sm:col-span-2`}>
          Title
          <input className={inputClass} name="title" defaultValue={module.title} required />
        </label>
        <label className={`${labelClass} sm:col-span-2`}>
          Description
          <input
            className={inputClass}
            name="description"
            defaultValue={module.description ?? ""}
          />
        </label>
        <label className={labelClass}>
          Title (Urdu)
          <input
            className={inputClass}
            name="title_ur"
            dir="rtl"
            defaultValue={module.translations?.ur?.title ?? ""}
          />
        </label>
        <label className={labelClass}>
          Description (Urdu)
          <input
            className={inputClass}
            name="description_ur"
            dir="rtl"
            defaultValue={module.translations?.ur?.description ?? ""}
          />
        </label>
        <label className={labelClass}>
          Status
          <select className={inputClass} name="status" defaultValue={module.status}>
            <option>draft</option>
            <option>published</option>
            <option>archived</option>
          </select>
        </label>
        <label className={labelClass}>
          Estimated minutes
          <input
            className={inputClass}
            name="estimated_minutes"
            type="number"
            min={0}
            defaultValue={module.estimated_minutes ?? ""}
          />
        </label>
        <div className="flex items-center gap-3 sm:col-span-2">
          <button className={primaryButtonClass} disabled={busy}>
            {busy && <Loader2 size={16} className="animate-spin" />}Save module
          </button>
          <button type="button" className={secondaryButtonClass} onClick={onCancel}>
            Close
          </button>
        </div>
      </form>
      {message && <InlineNotice tone={message.tone} text={message.text} />}
    </Card>
  );
}
